/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/useQaItpSigning.ts
 * FILE_ID: JOBHUB-QA-ITP-SIGNING-HOOK-001
 * PURPOSE: Place a signature on the live ITP. The document stays editable.
 */
import { useCallback, useRef, useState } from 'react'
import { countPdfPages } from '@quotech/email-hub/feature/lib/stampPdfSignatures'
import type { PresetSignature } from '@quotech/email-hub/feature/components/presetSignaturePlacement'
import type { EditorItem } from '@quotech/email-hub/feature/components/_pdfTypes'
import type { QaApi } from '../../../qa/qaApi'
import { signedPagesLabel, toPlacements, type ItpPlacement } from './itpPlacements'
import { resolveSignPages, withSignatureBand, type SignPageChoice } from './itpSignPages'
import { acceptMark, itemsFromPreset, rejectMark } from '../itp-sign/itpSignState'
import type { RenderedPage } from '@quotech/email-hub/feature/components/_pdfTypes'

type StoredSign = {
  bytes: Uint8Array
  fromHtml: string
  items: EditorItem[]
  base: Uint8Array
}

function copyBytes(source: Uint8Array): Uint8Array {
  const copy = new Uint8Array(source.byteLength)
  copy.set(source)
  return copy
}

export function useQaItpSigning({
  api,
  inspectionId,
  editedHtml,
}: {
  api: Pick<QaApi, 'renderItpPdf'>
  inspectionId: string
  editedHtml: string
}) {
  const [handSigned, setHandSigned] = useState<StoredSign | null>(null)
  const [padOpen, setPadOpen] = useState(false)
  const [handOpen, setHandOpen] = useState(false)
  const [handBytes, setHandBytes] = useState<Uint8Array | null>(null)
  const [handError, setHandError] = useState<string | null>(null)
  const [preset, setPreset] = useState<PresetSignature | null>(null)
  const [previewSigned, setPreviewSigned] = useState(false)
  const [previewBytes, setPreviewBytes] = useState<Uint8Array | null>(null)
  const [placeOn, setPlaceOn] = useState<SignPageChoice>('cover')
  const [editorKey, setEditorKey] = useState(0)
  const [openedItems, setOpenedItems] = useState<EditorItem[]>([])
  const [placements, setPlacements] = useState<ItpPlacement[]>([])
  const [items, setItems] = useState<EditorItem[]>([])
  const [pendingIds, setPendingIds] = useState<string[]>([])
  const [sessionPng, setSessionPng] = useState<Uint8Array | null>(null)
  const [signBytes, setSignBytes] = useState<Uint8Array | null>(null)
  const [inline, setInline] = useState(false)
  const [pageCount, setPageCount] = useState(1)
  const [hadCertificate, setHadCertificate] = useState(false)
  const cache = useRef<{ key: string; bytes: Uint8Array } | null>(null)
  const signFor = useRef('')

  const renderBase = useCallback(async () => {
    if (cache.current?.key === editedHtml) return cache.current.bytes
    const blob = await api.renderItpPdf(inspectionId, withSignatureBand(editedHtml))
    const bytes = new Uint8Array(await blob.arrayBuffer())
    cache.current = { key: editedHtml, bytes }
    return bytes
  }, [api, editedHtml, inspectionId])

  const resolveSignedBytes = useCallback(async (): Promise<Uint8Array | null> => {
    if (!handSigned) {
      if (placements.length === 0) return null
      // Reused from: same file (resolveSignedBytes edited-after-sign branch) so Print, Download and qDrive match Send.
      const blob = await api.renderItpPdf(inspectionId, withSignatureBand(editedHtml), placements)
      return new Uint8Array(await blob.arrayBuffer())
    }
    if (editedHtml === handSigned.fromHtml) return handSigned.bytes
    const blob = await api.renderItpPdf(inspectionId, withSignatureBand(editedHtml), placements)
    return new Uint8Array(await blob.arrayBuffer())
  }, [api, editedHtml, handSigned, inspectionId, placements])

  const reopen = useCallback(() => {
    if (!handSigned) return
    const drawn = handSigned.items.find((item) => item.kind === 'signature-drawn' || item.kind === 'signature-image')
    setPreset({
      pngBytes: drawn && 'imageBytes' in drawn ? drawn.imageBytes : new Uint8Array(),
      pageIndices: [],
      items: handSigned.items,
    })
    setHandBytes(copyBytes(handSigned.base))
    setHandError(null)
    setOpenedItems(handSigned.items)
    setHandOpen(true)
  }, [handSigned])

  const openEditor = useCallback(async () => {
    if (handSigned) {
      reopen()
      return
    }
    setHandError(null)
    setPreset(null)
    setOpenedItems([])
    try {
      const source = await renderBase()
      setHandBytes(copyBytes(source))
    } catch (err) {
      setHandError(err instanceof Error ? err.message : 'Could not open the editor')
      setHandBytes(null)
    }
    setHandOpen(true)
  }, [handSigned, renderBase, reopen])

  const onPadSaved = (pngBytes: Uint8Array) => {
    setPadOpen(false)
    void (async () => {
      setHandError(null)
      try {
        const source = handBytes && handBytes.byteLength > 0 ? handBytes : await renderBase()
        const copy = copyBytes(source)
        const count = await countPdfPages(copy)
        setPreset({
          pngBytes,
          pageIndices: resolveSignPages(placeOn, count).map((page) => page - 1),
          items: openedItems,
        })
        setHandBytes(copy)
        setEditorKey((current) => current + 1)
      } catch (err) {
        setHandError(err instanceof Error ? err.message : 'Could not open the editor')
      }
      setHandOpen(true)
    })()
  }

  const onHandSigned = (bytes: Uint8Array, items: EditorItem[] = []) => {
    const baseSource = handBytes ?? bytes
    const base = copyBytes(baseSource)
    const guessed = Math.max(1, ...items.map((item) => item.pageIndex + 1), 1)
    setHandSigned({ bytes, fromHtml: editedHtml, items, base })
    setPlacements(toPlacements(items, guessed))
    setPageCount(guessed)
    cache.current = null
    setHandOpen(false)
    setPreset(null)
    setPreviewSigned(false)
    setPreviewBytes(bytes)
    void (async () => {
      const baseCount = await countPdfPages(base).catch(() => guessed)
      const signedCount = await countPdfPages(bytes).catch(() => guessed)
      setPageCount(baseCount)
      setPlacements(toPlacements(items, baseCount))
      setHadCertificate(signedCount > baseCount)
    })()
  }

  const showSignedPreview = useCallback(async () => {
    const bytes = await resolveSignedBytes()
    setPreviewBytes(bytes)
    setPreviewSigned(true)
  }, [resolveSignedBytes])

  const ensureSignBytes = useCallback(async () => {
    if (signBytes && signFor.current === editedHtml) return signBytes
    const source = await renderBase()
    const copy = copyBytes(source)
    signFor.current = editedHtml
    setSignBytes(copy)
    return copy
  }, [editedHtml, renderBase, signBytes])

  const placePreset = useCallback((choice: SignPageChoice, pages: RenderedPage[], png?: Uint8Array) => {
    const source = png ?? sessionPng
    if (!source || pages.length === 0) return
    const created = itemsFromPreset(pages, source, choice)
    const next = [...items, ...created]
    setItems(next)
    setPendingIds((current) => [...current, ...created.map((item) => item.id)])
    setPlacements(toPlacements(next, pages.length))
    setPageCount(pages.length)
    setInline(true)
  }, [items, sessionPng])

  const acceptItem = (id: string) => setPendingIds((current) => acceptMark(current, id))

  const rejectItem = (id: string) => {
    const next = rejectMark(items, pendingIds, id)
    setItems(next.items)
    setPendingIds(next.pendingIds)
    setPlacements(toPlacements(next.items, pageCount))
  }

  const undoItem = () => {
    const last = items.at(-1)
    if (!last) return
    rejectItem(last.id)
  }

  const removeSignatures = () => {
    setHandSigned(null)
    setPlacements([])
    setItems([])
    setPendingIds([])
    setInline(false)
    setHadCertificate(false)
    cache.current = null
    setPreviewSigned(false)
    setPreviewBytes(null)
  }

  const rememberPng = (png: Uint8Array) => {
    setSessionPng(png)
    setPadOpen(false)
  }

  const replaceItems = (next: EditorItem[]) => {
    setItems(next)
    setPlacements(toPlacements(next, Math.max(pageCount, 1)))
    if (next.length > 0) setInline(true)
  }

  return {
    hasSignatures: handSigned != null || items.some((item) => item.kind !== 'text'),
    inline,
    items,
    pendingIds,
    sessionPng,
    signBytes,
    accepted: items.some((item) => item.kind !== 'text' && !pendingIds.includes(item.id)),
    count: handSigned ? 1 : 0,
    handStale: !!handSigned && handSigned.fromHtml !== editedHtml,
    certificateLost: hadCertificate && !!handSigned && handSigned.fromHtml !== editedHtml,
    signedPagesLabel: signedPagesLabel(placements, pageCount),
    placements,
    busy: padOpen || handOpen,
    padOpen,
    handOpen,
    handBytes,
    handError,
    preset,
    previewSigned,
    setPreviewSigned,
    previewBytes,
    placeOn,
    setPlaceOn,
    editorKey,
    openEditor,
    addSignatureFromBar: () => setPadOpen(true),
    onPadSaved,
    onHandSigned,
    canReopen: !!handSigned,
    reopen,
    removeSignatures,
    resolveSignedBytes,
    showSignedPreview,
    ensureSignBytes,
    placePreset,
    acceptItem,
    rejectItem,
    undoItem,
    rememberPng,
    openPad: () => setPadOpen(true),
    setItems,
    replaceItems,
    closePad: () => setPadOpen(false),
    closeHand: () => { setHandOpen(false); setHandBytes(null); setPreset(null) },
  }
}
